import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, AlertTriangle } from "lucide-react";
import { PowerBESSLogo } from "../components/PowerBESSLogo";
import "../styles/NotFoundPage.css";

export default function NotFoundPage() {
  const { pathname } = useLocation();

  return (
    <div className="nf-root" data-testid="not-found-page">
      <Helmet>
        <title>404 · Off the grid — PowerBESS.ai</title>
        <meta name="description" content="This route isn't in our 96-block schedule. Head back to the PowerBESS.ai home page or request a backtest." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {/* ─── Header ─── */}
      <header className="nf-header">
        <Link to="/" className="nf-logo" data-testid="nf-logo">
          <PowerBESSLogo size={28} />
          <span>Power<span className="nf-a">BESS</span>.ai</span>
        </Link>
        <Link to="/" className="nf-back" data-testid="nf-header-back">
          <ArrowLeft size={14} /> Back to home
        </Link>
      </header>

      {/* ─── Animated grid backdrop ─── */}
      <div className="nf-grid" aria-hidden="true">
        <div className="nf-grid-floor" />
        <div className="nf-grid-glow" />
      </div>

      {/* ─── Main ─── */}
      <main className="nf-main">
        <motion.div
          className="nf-c"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          {/* Status chip */}
          <div className="nf-eb" data-testid="nf-eyebrow">
            <span className="nf-eb-dot" />
            <span><b>ERROR 404</b> · ROUTE NOT FOUND</span>
            <span className="nf-eb-sep">·</span>
            <code className="nf-eb-path" title={pathname}>{pathname}</code>
          </div>

          {/* Glitchy 404 */}
          <div className="nf-bignum" aria-hidden="true">
            <span className="nf-bignum-bg">404</span>
            <span className="nf-bignum-fg">404</span>
          </div>

          {/* Headline */}
          <h1 className="nf-h1" data-testid="nf-title">
            Off the grid.<br /><span className="nf-a">This route isn&apos;t in our schedule.</span>
          </h1>

          <p className="nf-sub">
            Our optimizer runs a 96-block decision matrix every day &mdash; this address
            isn&apos;t in it. Most likely a moved link, a typo, or a route that no longer exists.
            The good news: the rest of the platform is online.
          </p>

          {/* Console-style readout */}
          <div className="nf-console" data-testid="nf-console">
            <div className="nf-console-row">
              <span className="nf-console-k">BLOCK</span>
              <span className="nf-console-v">404 / 96</span>
              <span className="nf-console-s nf-console-warn">OUT OF RANGE</span>
            </div>
            <div className="nf-console-row">
              <span className="nf-console-k">DISPATCH</span>
              <span className="nf-console-v">HOLD</span>
              <span className="nf-console-s">no matching route in registry</span>
            </div>
            <div className="nf-console-row">
              <span className="nf-console-k">FORECAST</span>
              <span className="nf-console-v">RETURN TO &nbsp;/</span>
              <span className="nf-console-s">expected resolution: instant</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="nf-ctas">
            <Link to="/" className="nf-btn nf-btn-primary" data-testid="nf-cta-home">
              <ArrowLeft size={15} />
              Back to home
            </Link>
            <Link to="/backtest-request" className="nf-btn nf-btn-ghost" data-testid="nf-cta-backtest">
              Request a backtest
              <ArrowRight size={15} />
            </Link>
          </div>

          {/* Useful links */}
          <div className="nf-links" data-testid="nf-quick-links">
            <span className="nf-links-l">Or jump to</span>
            <Link to="/#platform">Platform</Link>
            <span className="nf-links-sep" aria-hidden="true">/</span>
            <Link to="/#compliance">Compliance</Link>
            <span className="nf-links-sep" aria-hidden="true">/</span>
            <Link to="/#twin">Digital Twin</Link>
            <span className="nf-links-sep" aria-hidden="true">/</span>
            <Link to="/#faq">FAQ</Link>
          </div>
        </motion.div>
      </main>

      {/* ─── Footer mini-strip ─── */}
      <footer className="nf-footer" data-testid="nf-footer">
        <span className="nf-footer-warn"><AlertTriangle size={11} /> SIGNAL LOST</span>
        <span className="nf-footer-brand">PowerBESS.ai &middot; The Operating System for India&apos;s Energy Storage</span>
      </footer>
    </div>
  );
}
